import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote, Modal, Pill, relativeDate, formatQty } from '../components/ui.jsx';

const money = (cents) => (cents == null ? '' : `$${(cents / 100).toFixed(2)}`);

/**
 * Why there is no picture on screen. Four situations reach here and they must
 * not share a sentence: a photo saved but not served is not a catalogue with no
 * photo in it, and neither of those is a lookup that never happened. Only the
 * last one is worth trying again, so only the last one says so.
 */
function photoNoteFor(item, lookup) {
  if (item.image_url) return 'The photo saved for this item would not load.';
  if (lookup?.lookup_error) return 'Open Food Facts could not be reached just now. Worth another try in a moment.';
  if (lookup && !lookup.found) {
    return 'Open Food Facts has no photo for this barcode. Their catalogue is built by hand, so one may turn up later.';
  }
  return 'No photo yet.';
}

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const item = useApi(`/items/${id}`);
  const locations = useApi('/locations');
  const [editing, setEditing] = useState(false);
  const [linking, setLinking] = useState(false);
  // Which photograph would not load, rather than a bare flag: a lookup that
  // finds a different picture deserves its own attempt at drawing it.
  const [brokenPhoto, setBrokenPhoto] = useState(null);
  const [photoLookup, setPhotoLookup] = useState(null);
  const { busy, error, run } = useAction();

  if (item.loading && !item.data) return <Loading rows={5} />;
  if (item.error) return <ErrorNote error={item.error} onRetry={item.reload} />;
  if (!item.data) return null;

  const data = item.data;
  const nutrition = data.nutrition?.[0];

  const photoUrl = data.image_url && data.image_url !== brokenPhoto ? data.image_url : null;

  const findPhoto = () => run(async () => {
    const result = await api.post(`/items/${id}/photo`, {});
    setPhotoLookup(result);
    // Reloaded rather than patched in place, so the picture on screen is the
    // one that was actually saved.
    if (result.found) item.reload();
  });

  const removeAlias = (alias) => run(async () => {
    await api.delete(`/items/${id}/aliases/${alias.id}`);
    item.reload();
  });

  const archive = () => run(async () => {
    if (!window.confirm(`Archive “${data.name}”? Purchase history is kept.`)) return;
    await api.delete(`/items/${id}`);
    navigate('/items');
  });

  return (
    <div className="stack">
      <ErrorNote error={error} />

      <div>
        <h1>{data.name}</h1>
        <div className="small muted">
          {data.brand && `${data.brand} · `}
          {data.category}
          {data.package_size && ` · ${data.package_size} ${data.package_unit || ''}`}
        </div>
      </div>

      <div className="row wrap">
        {data.is_staple && <Pill>staple</Pill>}
        {data.shelf_life_days && <Pill>keeps {data.shelf_life_days}d</Pill>}
        {data.upc && <Pill>UPC {data.upc}</Pill>}
      </div>

      {/* Only where there is a photo, or a barcode that could find one. An item
          somebody typed in has no photo story to tell, and a section repeating
          "none" down a hand-built list would be noise. */}
      {(data.image_url || data.upc) && (
        <section>
          <div className="section-title">
            <h2>Photo</h2>
            {/* The lookup is by barcode and by nothing else, so the button is
                offered only where it could actually come back with something. */}
            {data.upc && (
              <button type="button" className="small ghost" disabled={busy} onClick={findPhoto}>
                {data.image_url ? 'check again' : 'find'}
              </button>
            )}
          </div>
          {photoUrl ? (
            <div className="card">
              {/* Decorative: the name is the heading directly above it. */}
              <img
                className="item-photo"
                src={photoUrl}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={() => setBrokenPhoto(photoUrl)}
              />
              <div className="tiny muted" style={{ marginTop: 8 }}>From Open Food Facts.</div>
            </div>
          ) : (
            <div className="card small muted">{photoNoteFor(data, photoLookup)}</div>
          )}
        </section>
      )}

      <section>
        <div className="section-title">
          <h2>In stock</h2>
          <span className="count">{data.inventory.length} location{data.inventory.length === 1 ? '' : 's'}</span>
        </div>
        {data.inventory.length === 0 ? (
          <div className="card small muted">None on hand.</div>
        ) : (
          <div className="card list">
            {data.inventory.map((row) => (
              <div key={row.id} className="list-item">
                <div className="grow">
                  <div>{formatQty(row.quantity, row.unit)}</div>
                  <div className="tiny muted">
                    {row.location_name}
                    {row.purchased_at && ` · bought ${relativeDate(row.purchased_at)}`}
                  </div>
                </div>
                {row.expires_at && <span className="tiny muted">exp {row.expires_at}</span>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="section-title">
          <h2>Nutrition</h2>
          <button type="button" className="small ghost" onClick={() => setLinking(true)}>
            {nutrition ? 'change' : 'link'}
          </button>
        </div>
        {nutrition ? (
          <div className="card">
            <div className="small muted truncate">{nutrition.description}</div>
            {/* An em dash, not `?? 0`: providers omit macros they did not
                measure, and "0g fat" is a claim while "—" is an absence. */}
            <div className="stat-grid" style={{ marginTop: 10 }}>
              <div className="stat">
                <div className="value">{nutrition.calories_per_100g == null ? '—' : Math.round(nutrition.calories_per_100g)}</div>
                <div className="label">cal / 100g</div>
              </div>
              <div className="stat">
                <div className="value">{nutrition.protein_g == null ? '—' : `${Math.round(nutrition.protein_g)}g`}</div>
                <div className="label">protein</div>
              </div>
              <div className="stat">
                <div className="value">{nutrition.carbs_g == null ? '—' : `${Math.round(nutrition.carbs_g)}g`}</div>
                <div className="label">carbs</div>
              </div>
              <div className="stat">
                <div className="value">{nutrition.fat_g == null ? '—' : `${Math.round(nutrition.fat_g)}g`}</div>
                <div className="label">fat</div>
              </div>
            </div>
            <div className="tiny muted" style={{ marginTop: 8 }}>
              Source: {nutrition.source} #{nutrition.source_ref}
            </div>
          </div>
        ) : (
          <div className="card small muted">Not linked to a nutrition record yet.</div>
        )}
      </section>

      <section>
        <div className="section-title">
          <h2>Receipt shorthand</h2>
          <span className="count">{data.aliases.length}</span>
        </div>
        {/* These are what makes the next scan of the same product match
            automatically instead of creating a duplicate. */}
        {data.aliases.length === 0 ? (
          <div className="card small muted">
            No aliases yet. They&rsquo;re learned automatically when you confirm a receipt.
          </div>
        ) : (
          <div className="card list">
            {data.aliases.map((alias) => (
              <div key={alias.id} className="list-item">
                <span className="grow mono truncate">{alias.alias_text}</span>
                <span className="tiny muted">{alias.source}</span>
                <button
                  type="button"
                  className="icon ghost"
                  aria-label={`Remove alias ${alias.alias_text}`}
                  onClick={() => removeAlias(alias)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {data.purchases.length > 0 && (
        <section>
          <div className="section-title"><h2>Purchase history</h2></div>
          <div className="card list">
            {data.purchases.map((purchase) => (
              <div key={purchase.id} className="list-item">
                <div className="grow">
                  <div className="small truncate mono">{purchase.raw_text}</div>
                  <div className="tiny muted">
                    {purchase.store_name || 'unknown store'} · {purchase.purchased_at}
                  </div>
                </div>
                <span className="small">{money(purchase.total_price_cents)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="row">
        <button type="button" className="grow" onClick={() => setEditing(true)}>Edit</button>
        <button type="button" className="danger" disabled={busy} onClick={archive}>Archive</button>
      </div>

      {editing && (
        <EditItem
          item={data}
          locations={locations.data || []}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); item.reload(); }}
        />
      )}

      {linking && (
        <LinkNutrition
          item={data}
          onClose={() => setLinking(false)}
          onSaved={() => { setLinking(false); item.reload(); }}
        />
      )}
    </div>
  );
}

function EditItem({ item, locations, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item.name,
    brand: item.brand || '',
    category: item.category,
    default_unit: item.default_unit,
    package_size: item.package_size ?? '',
    package_unit: item.package_unit || '',
    default_location_id: item.default_location_id ?? '',
    shelf_life_days: item.shelf_life_days ?? '',
    low_stock_threshold: item.low_stock_threshold ?? '',
    is_staple: Boolean(item.is_staple),
    upc: item.upc || '',
  });
  const { busy, error, run } = useAction();
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const save = () => run(async () => {
    await api.patch(`/items/${item.id}`, {
      ...form,
      package_size: form.package_size === '' ? null : Number(form.package_size),
      shelf_life_days: form.shelf_life_days === '' ? null : Number(form.shelf_life_days),
      low_stock_threshold: form.low_stock_threshold === '' ? null : Number(form.low_stock_threshold),
      default_location_id: form.default_location_id === '' ? null : form.default_location_id,
      is_staple: Boolean(form.is_staple),
    });
    onSaved();
  });

  const CATEGORIES = ['produce', 'meat', 'seafood', 'dairy', 'bakery', 'frozen',
    'pantry', 'spices', 'beverages', 'snacks', 'household', 'other'];

  return (
    <Modal title="Edit item" onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        <label><span className="label-text">Name</span>
          <input value={form.name} onChange={set('name')} /></label>
        <label><span className="label-text">Brand</span>
          <input value={form.brand} onChange={set('brand')} /></label>

        <div className="field-row">
          <label><span className="label-text">Category</span>
            <select value={form.category} onChange={set('category')}>
              {CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </label>
          <label><span className="label-text">Unit</span>
            <input value={form.default_unit} onChange={set('default_unit')} /></label>
        </div>

        <div className="field-row">
          <label><span className="label-text">Package size</span>
            <input type="number" inputMode="decimal" step="any" value={form.package_size} onChange={set('package_size')} /></label>
          <label><span className="label-text">Package unit</span>
            <input value={form.package_unit} onChange={set('package_unit')} placeholder="oz" /></label>
        </div>
        <p className="tiny muted" style={{ marginTop: -6 }}>
          Package size lets &ldquo;1 jar&rdquo; on the shelf be compared with &ldquo;2 tablespoons&rdquo; in a recipe.
        </p>

        <label><span className="label-text">Usually kept in</span>
          <select value={form.default_location_id} onChange={set('default_location_id')}>
            <option value="">No default</option>
            {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </select>
        </label>

        <div className="field-row">
          <label><span className="label-text">Shelf life (days)</span>
            <input type="number" inputMode="numeric" value={form.shelf_life_days} onChange={set('shelf_life_days')} /></label>
          <label><span className="label-text">Low stock at</span>
            <input type="number" inputMode="decimal" step="any" value={form.low_stock_threshold} onChange={set('low_stock_threshold')} /></label>
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.is_staple}
            onChange={(event) => setForm((f) => ({ ...f, is_staple: event.target.checked }))}
          />
          <span>Staple — track as present, not measured</span>
        </label>

        <label><span className="label-text">UPC</span>
          <input value={form.upc} onChange={set('upc')} inputMode="numeric" /></label>

        <button type="button" className="primary full" disabled={busy} onClick={save}>
          {busy ? <><span className="spinner" /> Saving</> : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

function LinkNutrition({ item, onClose, onSaved }) {
  const [query, setQuery] = useState([item.brand, item.name].filter(Boolean).join(' '));
  const [results, setResults] = useState(null);
  const { busy, error, run } = useAction();

  const search = () => run(async () => {
    const response = await api.get(`/nutrition/search?q=${encodeURIComponent(query)}&limit=10`);
    setResults(response.results);
  });

  const link = (ref) => run(async () => {
    await api.post(`/nutrition/item/${item.id}/link`, { ref });
    onSaved();
  });

  return (
    <Modal title="Link nutrition data" onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        <div className="row">
          <input
            className="grow"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') search(); }}
            aria-label="Search foods"
          />
          <button type="button" className="primary" disabled={busy} onClick={search}>Search</button>
        </div>

        <p className="tiny muted" style={{ margin: 0 }}>
          USDA FoodData Central. Lab-analyzed entries are listed before manufacturer-submitted ones.
        </p>

        {results?.length === 0 && <div className="small muted">No matches.</div>}

        {results?.length > 0 && (
          <div className="card list" style={{ maxHeight: 340, overflowY: 'auto' }}>
            {results.map((result) => (
              <button
                key={result.ref}
                type="button"
                className="ghost list-item"
                style={{ width: '100%', textAlign: 'left' }}
                disabled={busy}
                onClick={() => link(result.ref)}
              >
                <span className="grow" style={{ minWidth: 0 }}>
                  <span className="truncate" style={{ display: 'block' }}>{result.description}</span>
                  <span className="tiny muted truncate" style={{ display: 'block' }}>
                    {result.dataType}
                    {result.brand && ` · ${result.brand}`}
                    {result.preview?.calories_per_100g != null && ` · ${Math.round(result.preview.calories_per_100g)} cal/100g`}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
